import * as React from 'react';
import { PopupHeader } from '../../../../../common/components/PopupHeader/PopupHeader';
import classNames from 'classnames';
import Checkbox from '@mui/material/Checkbox';
import { Search } from '../../../../../common/components/Search/Search';
import { useEffect, useState } from 'react';
import { FormControlLabel, List, ListItem, ListItemText, Divider } from '@mui/material';
import { RestApi } from '../../../../../common/services/rest-api.service';
import { Common } from '../../../../../common/services/common.service';
import { getRows } from '../../../../../features/GridSlice';
import { showError, showSuccess } from '../../../../../features/AlertSlice';
import { useDispatch } from 'react-redux';
import { AppLoading } from '../../../../../common/components/appLoading/appLoading';
import { ToggleButtons } from 'common/components/ToggleButtons/ToggleButtons';
import { Text } from '@cyberpion/cyberpion-ui';
import './AddParkedDomainsPopup.scss';

const LIMIT = 10000;

export function AddParkedDomainsPopup(props: any) {
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [assets, setAssets] = useState<any>(null);
  const [filteredAssets, setFilteredAssets] = useState<any>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [currentView, setCurrentView] = useState<string>('single');
  const [inputError, setInputError] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [textArea, setTextArea] = useState<string>('');
  const [consent, setConsent] = useState<boolean>(false);
  const [consentError, setConsentError] = useState<boolean>(false);
  const [invalidAssets, setInvalidAssets] = useState<any>(null);

  useEffect(() => {
    setInputError(false);
    setErrorText('');
  }, [textArea]);

  const validate = (_input: string[]): boolean => {
    let hasError = false;

    if (!consent) {
      setConsentError(true);
      hasError = true;
    }

    const inputError = _input.filter((row: string) => {
      return !Common.validateTldr(row);
    });

    if (inputError.length) {
      hasError = true;
      setInputError(true);
      setErrorText(`Invalid Input: ${inputError[0]}`);
    }

    return !hasError;
  };

  const fetchAssets = (value = '') => {
    if (abortController) {
      abortController.abort();
    }
    const controller = new AbortController();
    setAbortController(controller);
    const url = `discovery/org-assets/?is_parked_domain=false&fields=asset&search=${value}`;
    RestApi.getData(url, false, controller.signal).subscribe(
      (response: any) => {
        setAssets(response.results);
        setFilteredAssets(response.results);
      },
      (error: any) => {
        if (error.name !== 'AbortError') {
          console.error('Request failed', error);
        }
      }
    );
  };

  useEffect(() => {
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatch = useDispatch<any>();

  const handleCheckboxChange = (item: string) => {
    setSelectedAssets(prevState => {
      if (prevState.includes(item)) {
        return prevState.filter(i => i !== item);
      }
      return [...prevState, item];
    });
  };

  const submitBulk = () => {
    const inputAsArray = textArea.split('\n');

    if (inputAsArray.length > LIMIT) {
      setErrorText(`List exceeded the number of items (max: ${LIMIT.toLocaleString()})`);
    }

    if (!validate(inputAsArray)) {
      return false;
    }

    const payload = {
      assets: inputAsArray,
      is_parked_domain: true,
      parked_domain_source: 'Manual'
    };

    setLoading(true);
    const url = Common.AddMsspAccountIfNeeded('settings/organization-management/parked-domains/');
    RestApi.setData(url, payload, 'POST').subscribe(
      (response: any) => {
        setInvalidAssets(null);
        setLoading(false);
        props.onClose();
        dispatch(getRows());
        const msg = 'Assets Added Successfully';
        dispatch(showSuccess(msg));
      },
      error => {
        if (error?.message) {
          setInvalidAssets(JSON.parse(error.message)?.failed_assets || null);
        }
        setLoading(false);
        dispatch(showError(error));
      }
    );
  };
  const submitSingle = () => {
    setLoading(true);
    const url = Common.AddMsspAccountIfNeeded('settings/organization-management/parked-domains/');
    const payload = {
      assets: selectedAssets,
      is_parked_domain: true,
      parked_domain_source: 'Manual'
    };
    RestApi.setData(url, payload, 'POST').subscribe(
      (response: any) => {
        setInvalidAssets(null);
        props.onClose();
        dispatch(getRows());
        dispatch(showSuccess('Updated Successfully'));
        setLoading(false);
      },
      (error: any) => {
        dispatch(showError('Failed to change asset status'));
        setLoading(false);
      }
    );
  };

  const submit = () => {
    if (currentView === 'single') {
      submitSingle();
    } else if (currentView === 'bulk') {
      submitBulk();
    }
  };

  const handleSearchOnChange = (value: string) => {
    if (value) {
      const filtered = assets.filter((item: any) => item.asset.toLowerCase().includes(value.toLowerCase()));
      setFilteredAssets(filtered);
    } else {
      setFilteredAssets(assets);
    }
  };

  const onToggle = (selectedOption: string) => {
    setSelectedAssets([]);
    setTextArea('');
    setInputError(false);
    setErrorText('');
    setConsent(false);
    setConsentError(false);
    setFilteredAssets(assets);
    setLoading(false);
    setInvalidAssets(null);
    setCurrentView(selectedOption.split(' ')[0].toLowerCase());
  };

  const checkboxText =
    'I confirm that all domains, URLs and managed domains are part or associated with my organization.';

  return (
    <div className="AddAssetsPopUp popup-inner-wrapper parkedDomains">
      <PopupHeader text="Add Additional Parked Domains" onClose={props.onClose} />
      <div className="popup-inner">
        <ToggleButtons options={['Single', 'Bulk']} onToggle={onToggle} />

        {currentView === 'single' && (
          <div style={{ marginTop: 16 }}>
            <div className="message">
              <div>Use the below boxes to define assets that will be assigned to the group.</div>
            </div>
            <Search onChange={handleSearchOnChange} />
            <List
              sx={{
                height: 300,
                overflowY: 'auto'
              }}
            >
              {!!filteredAssets?.length &&
                filteredAssets.map((item: any) => (
                  <>
                    <ListItem key={item.asset}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedAssets.includes(item.asset)}
                            onChange={() => handleCheckboxChange(item.asset)}
                            name={item.asset}
                          />
                        }
                        label={<ListItemText primary={item.asset} />}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </>
                ))}
            </List>
          </div>
        )}

        {currentView === 'bulk' && (
          <div style={{ marginTop: 16 }}>
            <div className="popup-inner">
              <div className="message">
                <div>Add here domains and subdomains to set them as parked.</div>
                <div>Each item should appear in a separate line.</div>
              </div>
              <textarea
                className={classNames({ error: inputError })}
                value={textArea}
                onChange={e => setTextArea(e.target.value)}
              ></textarea>
              {!!errorText && <div className="red">{errorText}</div>}
              {!!invalidAssets?.length && (
                <div>
                  <Text textSize={13} textColor="#F46666">
                    Invalid assets:
                  </Text>
                  <Text textSize={13} textColor="#F46666" style={{ whiteSpace: 'break-spaces' }}>
                    {invalidAssets.join(', ')}
                  </Text>
                </div>
              )}
              <div className={classNames('consent', { error: consentError })} onClick={() => setConsent(!consent)}>
                <input type="checkbox" checked={consent} /> {checkboxText}
              </div>
            </div>
          </div>
        )}
        <div className="buttons-wrapper">
          <button className="btn btn-big btn-secondary" onClick={props.onClose}>
            Cancel
          </button>
          <button
            className={classNames('btn', 'btn-big', 'btn-main', {
              disabled: !selectedAssets.length && (!textArea.length || !consent)
            })}
            onClick={submit}
          >
            Set As Parked
          </button>
        </div>
      </div>
      {loading && <AppLoading />}
    </div>
  );
}
